import { CrewmeisterApiV3 } from '../../apiV3';
import { NUMBER_OF_RETRIES } from '../../constants';

export enum POST_PROCESS_FUNCTION_NAME {
  SET_USER_TOKEN = 'SET_USER_TOKEN',
  REMOVE_USER_TOKEN = 'REMOVE_USER_TOKEN',
  SET_CREW_TOKEN = 'SET_CREW_TOKEN',
  REMOVE_CREW_TOKEN = 'REMOVE_CREW_TOKEN',
}

export enum HTTP_METHOD {
  GET = 'GET',
  POST = 'POST',
  PATCH = 'PATCH',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

export enum RSQL_OPERATOR {
  EQUALS = '==',
  GREATER_THAN_OR_EQUALS = '>=',
  LESSER_THAN_OR_EQUALS = '<=',
}

export type Resource = Record<string, any> & { '@type'?: string; }

export interface PersistentResource<I> extends Resource {
  id: I;
}

export interface Task<I, O> extends PersistentResource<string> {
  input: I;
  output?: O;
}

export type PatchResource = {
  operation: HTTP_METHOD;
  resource: Resource;
};

export interface Filter {
  [index: string]: number | Date | undefined;
  id?: number;
  crewId?: number;
  userId?: number;
  from?: Date;
  to?: Date;
}

export interface PageContext {
  [index: string]: number | undefined;
  page?: number;
  pageSize?: number;
}

export enum WriteRequestAction {
  CREATE = 'CREATE',
  REPLACE = 'REPLACE',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface WriteRequest<T extends PersistentResource<I>, I> {
  action: WriteRequestAction;
  id?: I;
  resource?: T;
  patchData?: { [key: string]: unknown };
}

export interface WriteResponse {
  createdAt: string;
}

export interface SyncWriteResponse<T extends PersistentResource<I>, I> extends WriteResponse {
  resourceAfterWrite?: T;
  resourceBeforeWrite?: T;
  request: WriteRequest<T, I>;
}

export interface ResourceFilter<T extends Resource> {
  matches: (resource: T) => boolean;
}

export enum ResourceFilterOperator {
  AND = 'AND',
  OR = 'OR',
  NOT = 'NOT',
}

export interface ResourceFilterTreeNode<T extends Resource> extends ResourceFilter<T> {
  children: ResourceFilter<T> | ResourceFilter<T>[];
  operator: ResourceFilterOperator;
}

export enum CompareBy {
  EQUAL_TO = 'EQUAL_TO',
  IS_NULL = 'IS_NULL',
  IS_NOT_NULL = 'IS_NOT_NULL',
  NOT_EQUAL_TO = 'NOT_EQUAL_TO',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_OR_EQUAL_THAN = 'GREATER_OR_EQUAL_THAN',
  LESS_THAN = 'LESS_THAN',
  LESS_OR_EQUAL_THAN = 'LESS_OR_EQUAL_THAN',
  IN = 'IN',
  NOT_IN = 'NOT_IN',
  LIST_CONTAINS = 'LIST_CONTAINS',
  LIST_NOT_CONTAINS = 'LIST_NOT_CONTAINS',
  CONTAINS = 'CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH = 'ENDS_WITH',
}

export interface ResourceFilterCompare<R extends Resource, T> extends ResourceFilter<R> {
  property: string;
  value: T | T[];
  compareBy: CompareBy;
}

export const ResourceFilterCompareBase = <R extends Resource, T>({ property, value, compareBy }: Pick<ResourceFilterCompare<R, T>, 'property' | 'value' | 'compareBy'>): ResourceFilterCompare<R, T> => ({
  property,
  value,
  compareBy,
  matches: (resource: R) => {
    switch (compareBy) {
      case CompareBy.EQUAL_TO:
        return resource[property] === value;
      case CompareBy.NOT_EQUAL_TO:
        return resource[property] !== value;
      case CompareBy.GREATER_THAN:
        return resource[property] > value;
      case CompareBy.GREATER_OR_EQUAL_THAN:
        return resource[property] >= value;
      case CompareBy.LESS_THAN:
        return resource[property] < value;
      case CompareBy.LESS_OR_EQUAL_THAN:
        return resource[property] <= value;
      case CompareBy.IN:
        return (value as R[]).includes(resource[property]);
      case CompareBy.NOT_IN:
        return !(value as R[]).includes(resource[property]);
      default:
        return false;
    }
  },
});

export const ResourceFilterAnd = <T extends Resource>(
  ...filters: ResourceFilter<T>[]
): ResourceFilterTreeNode<T> => ({
  matches: (resource: T) => filters.every((filter) => filter.matches(resource)),
  children: filters,
  operator: ResourceFilterOperator.AND,
});

export const ResourceFilterOr = <T extends Resource>(
  ...filters: ResourceFilter<T>[]
): ResourceFilterTreeNode<T> => ({
  matches: (resource: T) => filters.some((filter) => filter.matches(resource)),
  children: filters,
  operator: ResourceFilterOperator.OR,
});

export const ResourceFilterNot = <T extends Resource>(
  filter: ResourceFilter<T>
): ResourceFilterTreeNode<T> => ({
  matches: (resource: T) => !filter.matches(resource),
  children: filter,
  operator: ResourceFilterOperator.NOT,
});

export enum Direction {
  ASC = 'ASC',
  DESC = 'DESC',
}

export interface Order {
  property: string;
  direction: Direction;
  ignoreCase?: boolean;
}

export interface Sort {
  orders: Order[];
}

export interface Page<T extends Resource> {
  content: T[];
  number: number;
  numberOfElements: number;
  pageSize: number;
  sort: Sort;
}

export interface PageRequest {
  page?: number;
  pageSize?: number;
  sort?: Sort;
}

export interface ServiceMethods {
  getOne: (id: number | string, async?: boolean) => Promise<unknown>;
  get: (
    filter: Filter,
    operators: Record<string, RSQL_OPERATOR>,
    pageContext?: PageContext,
    async?: boolean,
    sortBy?: string
  ) => Promise<unknown>;
  read: <T extends Resource>(
    rsqlFilter: string,
    pageRequest: PageRequest,
  ) => Promise<Page<T>>;
  createOne: (resource: Resource, async?: boolean) => Promise<unknown>;
  create: (resources: Resource[], async?: boolean) => Promise<unknown>;
  patchOne: (id: number, patchData: Resource, async?: boolean) => Promise<unknown>;
  patch: (patchData: PatchResource[], async?: boolean) => Promise<unknown>;
  putOne: (id: number, resource: Resource, async?: boolean) => Promise<unknown>;
  put: (resources: Resource[], async?: boolean) => Promise<unknown>;
  deleteOne: (id: number, async?: boolean) => Promise<unknown>;
  delete: (ids: number[], async?: boolean) => Promise<unknown>;
}

export interface v3DefaultAction {
  apiV3: CrewmeisterApiV3;
}

export enum ERROR_CODES {
  FAILED_PRECONDITION = 'FAILED_PRECONDITION',
  OUT_OF_RANGE = 'OUT_OF_RANGE',
}

export enum ERROR_REASONS {
  INCOMPATIBLE = 'INCOMPATIBLE',
  NOT_EXISTING_OR_NO_PERMISSIONS = 'NOT_EXISTING_OR_NO_PERMISSIONS',
  TOO_BIG = 'TOO_BIG',
  UNAVAILABLE = 'UNAVAILABLE',
}
